import React, { useCallback, useMemo, useState } from "react";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";

export interface DateFieldProps {
  label: string;
  value: Date | string | null;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void | Promise<void>;
  id?: string;
  name?: string;
  required?: boolean;
  min?: string;
  max?: string;
  type?: "date" | "datetime-local";
}

export const DateField: React.FC<DateFieldProps> = ({
  label,
  value,
  id,
  onChange,
  name,
  required = false,
  min,
  max,
  type = "date",
}) => {
  const [error, setError] = useState<string>("");

  const formatValue = useCallback(
    (value: Date | string | null): string => {
      if (!value) return "";
      try {
        const date = value instanceof Date ? value : new Date(value);
        if (isNaN(date.getTime())) return "";
        return type === "date"
          ? date.toISOString().split("T")[0]
          : new Date(date.getTime() - date.getTimezoneOffset() * 60000)
              .toISOString()
              .slice(0, 16);
      } catch {
        return "";
      }
    },
    [type]
  );

  const formattedValue = useMemo(() => formatValue(value), [value, formatValue]);

  const validateDate = (date: string) => {
    if (required && !date) return "Este campo es obligatorio";
    if (min && date < min) return `Debe ser después de ${min}`;
    if (max && date > max) return `Debe ser antes de ${max}`;
    return "";
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setError(validateDate(newValue));
    onChange(e);
  };

  return (
    <div className="flex flex-col space-y-1">
      <label className="text-sm font-medium text-gray-700" htmlFor={id || name}>
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="relative">
        <input
          type={type}
          value={formattedValue}
          onChange={handleChange}
          id={id || name}
          name={name}
          required={required}
          min={min}
          max={max}
          className={`w-full rounded-md py-2 px-3 transition-colors duration-200 ${
            error
              ? "border-2 border-red-500 focus:ring-red-500 focus:border-red-500"
              : "border border-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          }`}
        />
        {error && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
          </div>
        )}
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};
